import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { EditableListItem, Octopus } from '@maximizer/core/shared/domain';
import { ContextService } from '@maximizer/core/shared/data-access';

type ModifyResult = {
  successful: boolean;
  item: EditableListItem<string> | null;
};

type RelatedEntryRequest = Octopus.Request & {
  Workflow: Octopus.ReadRequest<Octopus.Workflow>;
};

type RelatedEntryResponse = Octopus.Response & {
  Workflow: Octopus.DataResponse<Octopus.Workflow>;
};

@Injectable()
export class CategoryService {
  constructor(
    private readonly http: HttpClient,
    private readonly context: ContextService,
  ) {}

  get(): Observable<EditableListItem<string>[]> {
    const request: Octopus.WorkflowFieldOptionRequest = {
      Workflow: {
        FieldOptions: {
          Category: [
            {
              Key: 1,
              DisplayValue: 1,
              InActive: 1,
            },
          ],
        },
        Options: {
          ShowInactive: true,
        },
      },
    };

    return this.http
      .post<Octopus.WorkflowFieldOptionResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (
            result.Code === Octopus.ResponseStatusCode.Successful &&
            result.Workflow.FieldOptions.Category
          ) {
            const mapper = new Octopus.WorkflowPropertyMapper();

            return result.Workflow.FieldOptions.Category.map((item) =>
              mapper.mapCategoryForEdit(item),
            );
          }
          return [];
        }),
      );
  }

  add(
    category: EditableListItem<string>,
  ): Observable<EditableListItem<string> | null> {
    const request: Octopus.WorkflowFieldOptionWriteRequest = {
      Workflow: {
        FieldOptions: {
          Category: [
            {
              Key: null,
              Name: category.name,
              DisplayValue: category.name,
              InActive: false,
            },
          ],
        },
        Options: {
          Append: true,
        },
      },
    };

    return this.http
      .post<Octopus.WorkflowFieldOptionResponse>(
        `${this.context.api}${Octopus.Action.CREATE}`,
        request,
      )
      .pipe(
        map((result) => {
          if (
            result.Code === Octopus.ResponseStatusCode.Successful &&
            result.Workflow.FieldOptions.Category?.length
          ) {
            const mapper = new Octopus.WorkflowPropertyMapper();

            return mapper.mapCategoryForEdit(
              result.Workflow.FieldOptions.Category[0],
            );
          }
          return null;
        }),
      );
  }

  checkDuplicatedName(name: string): Observable<boolean> {
    const request: Octopus.WorkflowFieldOptionReadRequest = {
      Workflow: {
        FieldOptions: {
          Category: {
            Scope: {
              Fields: {
                Key: 1,
              },
            },
            Criteria: {
              SearchQuery: {
                Name: {
                  $EQ: name,
                },
              },
            },
          },
        },
        Options: {
          ShowInactive: true,
        },
      },
    };

    return this.http
      .post<Octopus.WorkflowFieldOptionReadResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (
            result.Code === Octopus.ResponseStatusCode.Successful &&
            result.Workflow.FieldOptions.Category?.Data
          ) {
            return result.Workflow.FieldOptions.Category?.Data.length > 0;
          }
          return false;
        }),
      );
  }

  checkUse(category: string): Observable<boolean> {
    const request: RelatedEntryRequest = {
      Workflow: {
        Criteria: {
          SearchQuery: {
            Category: {
              $EQ: category,
            },
          },
          Top: 1,
        },
        Scope: {
          Fields: {
            Key: 1,
          },
        },
      },
    };

    return this.http
      .post<RelatedEntryResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          return (
            result.Code === Octopus.ResponseStatusCode.Successful &&
            result.Workflow.Data.length > 0
          );
        }),
      );
  }

  update(
    category: EditableListItem<string>,
  ): Observable<EditableListItem<string> | null> {
    return this.modify(category, { Modify: true }).pipe(
      map((result) => result.item),
    );
  }

  delete(category: EditableListItem<string>): Observable<boolean> {
    return this.modify(category, { Remove: true }).pipe(
      map((result) => result.successful),
    );
  }

  private modify(
    category: EditableListItem<string>,
    operation: Octopus.FieldOptionsWriteOperation,
  ): Observable<ModifyResult> {
    const request: Octopus.WorkflowFieldOptionWriteRequest = {
      Workflow: {
        FieldOptions: {
          Category: [
            {
              Key: category.id,
              Name: category.name,
              DisplayValue: category.name,
              InActive: !category.isActive,
            },
          ],
        },
        Options: operation,
      },
    };

    return this.http
      .post<Octopus.WorkflowFieldOptionResponse>(
        `${this.context.api}${Octopus.Action.UPDATE}`,
        request,
      )
      .pipe(
        map((result) => {
          const modify: ModifyResult = {
            successful: result.Code === Octopus.ResponseStatusCode.Successful,
            item: null,
          };

          if (
            modify.successful &&
            result.Workflow?.FieldOptions.Category?.length
          ) {
            const mapper = new Octopus.WorkflowPropertyMapper();

            modify.item = mapper.mapCategoryForEdit(
              result.Workflow.FieldOptions.Category[0],
            );
          }
          return modify;
        }),
      );
  }
}
